'use client';

import { type PropsWithChildren } from 'react';
import { FinderProviders } from '@frameio/assets';
import { BulkAssetOperationsContextProvider } from '@frameio/assets/bulk-operations/contexts';
import { BulkOperationsMoveAssetsContextProvider } from '@frameio/assets/bulk-operations/contexts/BulkOperationsMoveAssetsContext';
import { FinderAssetSelection } from '@frameio/assets/finder';
import { AssetOperationsContextProvider } from '@frameio/assets/operations/contexts/AssetOperationsContext';
import { FinderRefetchProvider } from '@frameio/components/finder-refetch/components/FinderRefetchProvider';
import {
  AccountSubscriptions,
  UserSubscriptions,
} from '@frameio/components/subscriptions';
import { HostActiveEntitiesProvider } from '@frameio/integrations/host';
import { AccountPermissionsQuery } from '@frameio/integrations/shared/components/AccountPermissionsQuery';
import { HostMediaViewerConfigProvider } from '@frameio/integrations/shared/components/HostMediaViewerConfigProvider';
import { FeatureGatesContextProvider } from '@frameio/plan-management/feature-gate/contexts';
import { PlanManagementModalProvider } from '@frameio/plan-management/modal/components';
import { WhatsNewProvider } from '@frameio/primary-nav/components/AppSheet/Sheets/WhatsNewSheet/WhatsNewContext';
import { QuickSearchContextProvider } from '@frameio/quick-search/contexts';
import { SearchContextProvider } from '@frameio/search/shared/contexts';
import { SearchAnalyticsProvider } from '@frameio/search/shared/contexts/SearchAnalyticsContext';
import { SelectionContextProvider } from '@frameio/selection';
import { CommandRegistryContextProvider } from '@frameio/shared/commands/components/CommandRegistryContext';
import { CommandRegistryHelpDialogContextProvider } from '@frameio/shared/commands/components/CommandRegistryHelpDialogContext';
import { FirstLoginProvider } from '@frameio/shared/contexts';
import { ActiveAccountIdContextProvider } from '@frameio/shared/contexts/ActiveAccountIdContext/ActiveAccountIdContext';
import { ActiveFolderIdContext } from '@frameio/shared/contexts/ActiveEntitiesContext';
import { ActiveProjectIdFromParams } from '@frameio/shared/contexts/ActiveEntitiesContext/components';
import { AppSheetContextProvider } from '@frameio/shared/contexts/AppSheetContext';
import { AssetsContextProvider } from '@frameio/shared/contexts/AssetsContext/AssetsContext';
import { ModalContextProvider } from '@frameio/shared/contexts/ModalContext/ModalContext';
import { PanelLayoutContextProvider } from '@frameio/shared/contexts/PanelLayoutContext';
import { FeatureFlagsContextProviderWrapper } from '@frameio/shared/feature-flags';
import { NavigationHistoryProvider } from '@frameio/shared/integrations/contexts/NavigationHistoryContext';
import { AppUrlParamsProvider } from '@frameio/shared/routing/params/AppUrlParamProvider';
import { UploadLinkModalProvider } from '@frameio/upload-links';
import { RecentProjectsProvider } from '@frameio/workspaces/shared';
import { WorkspacesProjectFilterContextProvider } from '@frameio/workspaces/shared/components/WorkspaceOverflowMenu/WorkspacesProjectFilterContextProvider';

export const AuthenticatedProviders = (props: PropsWithChildren) => {
  return (
    <PanelLayoutContextProvider>
      <ActiveAccountIdContextProvider>
        <AccountPermissionsQuery />
        <FeatureFlagsContextProviderWrapper mode="app">
          <FeatureGatesContextProvider mode="app">
            <FirstLoginProvider>
              <CommandRegistryContextProvider>
                <CommandRegistryHelpDialogContextProvider isEnabled={false}>
                  <SelectionContextProvider>
                    <AssetsContextProvider>
                      <AssetOperationsContextProvider>
                        <ModalContextProvider>
                          <AppSheetContextProvider>
                            <NavigationHistoryProvider>
                              <ActiveProjectIdFromParams>
                                <FinderRefetchProvider>
                                  <HostActiveEntitiesProvider>
                                    <HostMediaViewerConfigProvider>
                                      <ActiveFolderIdContext>
                                        <AppUrlParamsProvider>
                                          <QuickSearchContextProvider>
                                            <SearchAnalyticsProvider>
                                              <SearchContextProvider>
                                                <SelectionContextProvider>
                                                  <BulkAssetOperationsContextProvider>
                                                    <FinderAssetSelection />
                                                    <BulkOperationsMoveAssetsContextProvider>
                                                      <AssetOperationsContextProvider>
                                                        <FinderProviders>
                                                          <BulkAssetOperationsContextProvider>
                                                            <WorkspacesProjectFilterContextProvider>
                                                              <RecentProjectsProvider>
                                                                <AccountSubscriptions />
                                                                <UserSubscriptions />
                                                                <WhatsNewProvider>
                                                                  <PlanManagementModalProvider>
                                                                    <UploadLinkModalProvider>
                                                                      {
                                                                        props.children
                                                                      }
                                                                    </UploadLinkModalProvider>
                                                                  </PlanManagementModalProvider>
                                                                </WhatsNewProvider>
                                                              </RecentProjectsProvider>
                                                            </WorkspacesProjectFilterContextProvider>
                                                          </BulkAssetOperationsContextProvider>
                                                        </FinderProviders>
                                                      </AssetOperationsContextProvider>
                                                    </BulkOperationsMoveAssetsContextProvider>
                                                  </BulkAssetOperationsContextProvider>
                                                </SelectionContextProvider>
                                              </SearchContextProvider>
                                            </SearchAnalyticsProvider>
                                          </QuickSearchContextProvider>
                                        </AppUrlParamsProvider>
                                      </ActiveFolderIdContext>
                                    </HostMediaViewerConfigProvider>
                                  </HostActiveEntitiesProvider>
                                </FinderRefetchProvider>
                              </ActiveProjectIdFromParams>
                            </NavigationHistoryProvider>
                          </AppSheetContextProvider>
                        </ModalContextProvider>
                      </AssetOperationsContextProvider>
                    </AssetsContextProvider>
                  </SelectionContextProvider>
                </CommandRegistryHelpDialogContextProvider>
              </CommandRegistryContextProvider>
            </FirstLoginProvider>
          </FeatureGatesContextProvider>
        </FeatureFlagsContextProviderWrapper>
      </ActiveAccountIdContextProvider>
    </PanelLayoutContextProvider>
  );
};
